import React from "react";
import {Quill} from "quill";
import {
  AditusType,
  AspType,
  ExpoPlatformType,
  IExpoPlatform,
  IJet,
  IShow,
  JetType,
  SitkaType,
  VisitType,
} from "@evennt-tech/api-v2-core";
import filterNotEmptyArray from "tools/common/filterNotEmptyArray";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import CodeIcon from "@material-ui/icons/Code";
import VisitIcon from "view/images/VisitIcon";
import clsx from "clsx";
import {externalRedirectPlaceholderHref} from "view/components/RichText/tools/externalRedirectLink";
import AditusIcon from "view/images/AditusIcon";
import SitkaIcon from "view/images/SitkaIcon";

import aspLogo from "assets/images/show/aspLogo.jpg";
import expoPlatformLogo from "assets/images/show/ExpoPlatform.png";
import jetLogo from "view/images/integrations/jet.png";
import findLinkRun from "../helpers/findLinkRun";
import {
  armPendingLink,
  disarmPendingLink,
  isPendingLink,
} from "../helpers/pendingLink";
import useStyles from "../styles";

export enum ExternalControlType {
  asp = "asp",
  aditus = "aditus",
  sitka = "sitka",
  visit = "visit",
  expoPlatform = "expoPlatform",
  jet = "jet",
}

/**
 * Every integration. Passed where there is no Show whose config could narrow
 * the list — a library master section, authored once and read on many Shows;
 * the reader is told at click time when their Show has one switched off.
 */
export const ALL_EXTERNAL_CONTROLS = Object.values(ExternalControlType);

/**
 * The integrations a Show can insert a link to, in the state that makes the
 * link work. JET is gated on its own fetched config — it is not part of the
 * Show payload — and a Sync Issue (`lastError`) hides it.
 */
export const getShowIntegrationControls = ({
  show,
  expoPlatform,
  jetConfig,
}: {
  show?: IShow;
  expoPlatform?: IExpoPlatform;
  jetConfig?: IJet | null;
}): ExternalControlType[] =>
  [
    expoPlatform?.type === ExpoPlatformType.Pushing
      ? ExternalControlType.expoPlatform
      : undefined,
    show?.asp?.type === AspType.Pushing ? ExternalControlType.asp : undefined,
    show?.visit?.type === VisitType.Pushing
      ? ExternalControlType.visit
      : undefined,
    show?.aditus?.type === AditusType.Pushing
      ? ExternalControlType.aditus
      : undefined,
    show?.sitka?.type === SitkaType.SSO ? ExternalControlType.sitka : undefined,
    jetConfig?.type === JetType.Pushing && !jetConfig?.lastError
      ? ExternalControlType.jet
      : undefined,
  ].filter(filterNotEmptyArray);

const labelMap = {
  [ExternalControlType.asp]: "ASP",
  [ExternalControlType.visit]: "Visit",
  [ExternalControlType.aditus]: "Aditus",
  [ExternalControlType.sitka]: "Sitka",
  [ExternalControlType.expoPlatform]: "Expo Platform",
  [ExternalControlType.jet]: "JET",
};

/*
 * Must stay a component: render() below is invoked as a plain function inside
 * the editor's render, so a hook called there would bind to the editor and
 * break when the set of visible controls changes.
 */
const ExternalControlButton: React.FC<{type: ExternalControlType}> = ({
  type,
}) => {
  const classes = useStyles();
  const className = `ql-${type}Link`;
  const title = `Create link to ${labelMap[type]}`;
  const logoMap = {
    [ExternalControlType.asp]: (
      <img src={aspLogo} alt="ASP" className={classes.integrationLogo} />
    ),
    [ExternalControlType.visit]: (
      <VisitIcon className={classes.integrationLogo} />
    ),
    [ExternalControlType.aditus]: (
      <AditusIcon className={classes.integrationLogo} />
    ),
    [ExternalControlType.sitka]: (
      <SitkaIcon className={classes.integrationLogo} />
    ),
    [ExternalControlType.expoPlatform]: (
      <img
        src={expoPlatformLogo}
        alt="ASP"
        className={classes.integrationLogo}
      />
    ),
    [ExternalControlType.jet]: (
      <img src={jetLogo} alt="JET" className={classes.integrationLogo} />
    ),
  };
  return (
    <Tooltip title={title}>
      <IconButton
        style={{padding: 0}}
        className={clsx(className, classes.integrationButton)}
      >
        <CodeIcon />
        {logoMap[type]}
      </IconButton>
    </Tooltip>
  );
};

export const getExternalControl = (type: ExternalControlType) => ({
  render: () => <ExternalControlButton key={type} type={type} />,
  /*
   * A format toggle, like bold, that never inserts text: a selection is linked
   * to the integration, or unlinked when it already is. With only a caret, the
   * link is switched on for what is typed there next, and clicked again it is
   * switched off; a caret inside this integration's link removes that link.
   *
   * The href is a Show-agnostic placeholder resolved when the description is
   * read, so the same link works on every Show a library section is assigned to.
   */
  handler: (editor: Quill) => {
    const range = editor.getSelection(true) ?? {
      index: Math.max(0, editor.getLength() - 1),
      length: 0,
    };
    const href = externalRedirectPlaceholderHref(type);

    if (range.length) {
      disarmPendingLink(editor);
      const isLinked = editor.getFormat(range).link === href;
      editor.formatText(
        range.index,
        range.length,
        "link",
        isLinked ? false : href,
        "user"
      );
      return;
    }

    if (isPendingLink(editor, href, range.index)) {
      disarmPendingLink(editor);
      return;
    }

    const run = findLinkRun(editor.getContents().ops, range.index);
    if (run?.href === href && range.index < run.index + run.length) {
      disarmPendingLink(editor);
      editor.formatText(run.index, run.length, "link", false, "user");
      return;
    }

    armPendingLink(editor, href, range.index);
  },
});
